import React, { useState } from 'react';
import { format } from 'date-fns';
import { Edit, Trash2, Calendar, User, Heart, MessageSquare, Send, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardFooter } from '../ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { feedAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const FeedPostCard = ({ post, company, onEdit, onDelete, onRefresh }) => {
    const { user } = useAuth();
    const [showComments, setShowComments] = useState(false);
    const [newComment, setNewComment] = useState('');
    const [isLiking, setIsLiking] = useState(false);
    const [isCommenting, setIsCommenting] = useState(false);

    const formattedDate = format(new Date(post.created_at), 'PPP');
    const isLiked = post.likes?.includes(user?.id);
    const likesCount = post.likes?.length || 0;
    const commentsCount = post.comments?.length || 0;

    const handleToggleLike = async () => {
        if (isLiking) return;
        try {
            setIsLiking(true);
            await feedAPI.toggleLike(post.id);
            onRefresh(true);

        } catch (error) {
            toast.error('Failed to update like');
        } finally {
            setIsLiking(false);
        }
    };

    const handleAddComment = async (e) => {
        e.preventDefault();
        if (!newComment.trim() || isCommenting) return;

        try {
            setIsCommenting(true);
            await feedAPI.addComment(post.id, newComment);
            setNewComment('');
            toast.success('Comment added');
            onRefresh(true);
        } catch (error) {
            toast.error('Failed to add comment');
        } finally {
            setIsCommenting(false);
        }
    };

    const handleDeleteComment = async (commentId) => {
        try {
            await feedAPI.deleteComment(post.id, commentId);
            toast.success('Comment removed');
            onRefresh(true);

        } catch (error) {
            toast.error('Failed to remove comment');
        }
    };

    const getLogoUrl = (logoPath) => {
        if (!logoPath) return null;
        if (logoPath.startsWith('http')) return logoPath;
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
        return `${backendUrl}${logoPath}`;
    };

    return (
        <Card className="overflow-hidden border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200">
            <CardContent className="pt-6 pb-2 space-y-4">
                {/* Header Information moved inside Body */}
                <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10 border border-slate-100 shadow-sm">
                            {post.author_avatar ? (
                                <AvatarImage src={`${process.env.REACT_APP_BACKEND_URL}${post.author_avatar}`} alt={post.author_name} className="object-contain" />
                            ) : company?.logo ? (
                                <AvatarImage src={getLogoUrl(company.logo)} alt={company.name} className="object-contain" />
                            ) : null}
                            <AvatarFallback className="bg-primary/10 text-primary font-bold">
                                {post.author_name ? post.author_name.substring(0, 2).toUpperCase() : 'CO'}
                            </AvatarFallback>
                        </Avatar>
                        <div>
                            <div className="flex items-center gap-2">
                                <span className="font-bold text-slate-900">{company?.name || 'Company Admin'}</span>
                                {!post.is_published && (
                                    <Badge variant="secondary" className="bg-amber-100 text-amber-700 hover:bg-amber-100 border-none text-[10px] h-4">Draft</Badge>
                                )}
                            </div>
                            <div className="flex items-center text-[11px] text-slate-400 gap-3">
                                <div className="flex items-center gap-1">
                                    <Calendar className="h-3 w-3" />
                                    <span>{formattedDate}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-1">
                        {onEdit && (
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400 hover:text-primary" onClick={() => onEdit(post)}>
                                <Edit className="h-3.5 w-3.5" />
                            </Button>
                        )}
                        {onDelete && (
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400 hover:text-destructive" onClick={() => onDelete(post.id)}>
                                <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                        )}
                    </div>
                </div>

                {/* Main Content */}
                <div className="prose prose-slate max-w-none">
                    <div
                        className="feed-content ql-viewer text-slate-800"
                        dangerouslySetInnerHTML={{ __html: post.content }}
                    />
                </div>
            </CardContent>

            <CardFooter className="flex flex-col border-t p-0">
                <div className="flex items-center gap-6 px-4 py-2 w-full">
                    <button
                        onClick={handleToggleLike}
                        className={cn(
                            "flex items-center gap-2 text-sm font-medium transition-colors p-2 rounded-md hover:bg-slate-50",
                            isLiked ? "text-rose-600" : "text-slate-600 hover:text-slate-900"
                        )}
                    >
                        <Heart className={cn("h-5 w-5", isLiked && "fill-current")} />
                        <span>{likesCount} {likesCount === 1 ? 'Like' : 'Likes'}</span>
                    </button>

                    <button
                        onClick={() => setShowComments(!showComments)}
                        className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors p-2 rounded-md hover:bg-slate-50"
                    >
                        <MessageSquare className="h-5 w-5" />
                        <span>{commentsCount} {commentsCount === 1 ? 'Comment' : 'Comments'}</span>
                    </button>
                </div>

                {showComments && (
                    <div className="w-full bg-slate-50/50 border-t p-4 space-y-4">
                        {/* Comments List */}
                        <div className="space-y-4">
                            {post.comments?.map((comment) => (
                                <div key={comment.id} className="flex gap-3 group">
                                    <Avatar className="h-8 w-8 shrink-0">
                                        {comment.author_avatar ? (
                                            <AvatarImage src={`${process.env.REACT_APP_BACKEND_URL}${comment.author_avatar}`} alt={comment.author_name} className="object-contain" />
                                        ) : null}
                                        <AvatarFallback className="bg-slate-200 text-slate-600 text-[10px] font-bold">
                                            {comment.author_name?.substring(0, 2).toUpperCase()}
                                        </AvatarFallback>
                                    </Avatar>
                                    <div className="flex-1 bg-white p-3 rounded-2xl rounded-tl-none border border-slate-100 shadow-sm relative">
                                        <div className="flex justify-between items-start mb-1">
                                            <span className="text-xs font-bold text-slate-900">{comment.author_name}</span>
                                            <div className="flex items-center gap-2">
                                                <span className="text-[10px] text-slate-400">
                                                    {format(new Date(comment.created_at), 'MMM d, h:mm a')}
                                                </span>
                                                {(comment.author_id === user?.id || user?.role === 'COMPANY_ADMIN') && (
                                                    <button
                                                        onClick={() => handleDeleteComment(comment.id)}
                                                        className="text-slate-300 hover:text-destructive opacity-0 group-hover:opacity-100 transition-all"
                                                    >
                                                        <X className="h-3 w-3" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <p className="text-sm text-slate-700 leading-relaxed">{comment.content}</p>
                                    </div>
                                </div>
                            ))}

                            {commentsCount === 0 && (
                                <p className="text-center text-xs text-slate-400 py-2">No comments yet. Be the first to comment!</p>
                            )}
                        </div>

                        {/* Add Comment Input */}
                        <form onSubmit={handleAddComment} className="flex gap-2 pt-2">
                            <Input
                                placeholder="Write a comment..."
                                value={newComment}
                                onChange={(e) => setNewComment(e.target.value)}
                                className="bg-white"
                            />
                            <Button type="submit" size="icon" disabled={!newComment.trim() || isCommenting}>
                                <Send className="h-4 w-4" />
                            </Button>
                        </form>
                    </div>
                )}
            </CardFooter>
        </Card>
    );
};

export default FeedPostCard;
